import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Users, HardDrive, FolderOpen, TrendingUp } from 'lucide-react';
import { getBooks } from '../api/books';
import { getStats } from '../api/stats';
import { BookCard } from '../components/BookCard';
import { LoadingScreen } from '../components/ui/Spinner';
import { useToastStore } from '../store/toastStore';
import type { Book, Stats } from '../types/models';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function HomePage() {
  const [recentBooks, setRecentBooks] = useState<Book[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const addToast = useToastStore((state) => state.addToast);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [booksData, statsData] = await Promise.all([
          getBooks({ limit: 6 }),
          getStats(),
        ]);

        setRecentBooks(booksData);
        setStats(statsData);
      } catch (err) {
        addToast('Failed to load library data', 'error');
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [addToast]);

  if (loading) {
    return <LoadingScreen message="Loading library..." />;
  }

  return (
    <div className="home-page">
      <header className="page-header">
        <h1>
          {/* <Library className="icon icon-lg" aria-hidden="true" /> */}
          Freeman
        </h1>
        <p className="subtitle">Family book collection</p>
      </header>

      {stats && (
        <section className="stats-section">
          <div className="stat-card">
            <div className="stat-icon">
              <BookOpen className="icon icon-lg" aria-hidden="true" />
            </div>
            <div className="stat-content">
              <p className="stat-number">{(stats.books ?? 0).toLocaleString()}</p>
              <p className="stat-label">Total Books</p>
            </div>
          </div>

          {stats.total_storage != null && (
            <div className="stat-card">
              <div className="stat-icon">
                <HardDrive className="icon icon-lg" aria-hidden="true" />
              </div>
              <div className="stat-content">
                <p className="stat-number">{formatBytes(stats.total_storage)}</p>
                <p className="stat-label">Library Size</p>
              </div>
            </div>
          )}

          {stats.authors != null && (
            <div className="stat-card">
              <div className="stat-icon">
                <Users className="icon icon-lg" aria-hidden="true" />
              </div>
              <div className="stat-content">
                <p className="stat-number">{stats.authors.toLocaleString()}</p>
                <p className="stat-label">Authors</p>
              </div>
            </div>
          )}

          {stats.collections != null && (
            <div className="stat-card">
              <div className="stat-icon">
                <FolderOpen className="icon icon-lg" aria-hidden="true" />
              </div>
              <div className="stat-content">
                <p className="stat-number">{stats.collections}</p>
                <p className="stat-label">Collections</p>
              </div>
            </div>
          )}

          {stats.recent_books != null && stats.recent_books > 0 && (
            <div className="stat-card stat-highlight">
              <div className="stat-icon">
                <TrendingUp className="icon icon-lg" aria-hidden="true" />
              </div>
              <div className="stat-content">
                <p className="stat-number">+{stats.recent_books}</p>
                <p className="stat-label">Added This Month</p>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="recent-section">
        <div className="section-header">
          <h2>Recently Added</h2>
        </div>

        {recentBooks.length === 0 ? (
          <div className="empty-state">
            <p>No books yet. Start by uploading your first book!</p>
            <Link to="/upload" className="btn btn-primary">
              Upload Book
            </Link>
          </div>
        ) : (
          <div className="book-grid">
            {recentBooks.map((book) => (
              <BookCard key={book.id} book={book} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
